import React, { useState, useEffect } from 'react';
import { Card, Typography, Button, Form, Input, Select, InputNumber, message, Spin } from 'antd';
import { ArrowLeftOutlined, SaveOutlined } from '@ant-design/icons';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { getDeviceById, getProjectById, createDevice, updateDevice, getFormConfigById } from '../../services/api';
import { DynamicForm, FormConfig, FormFieldConfig } from '../../components/DynamicForm';
import './styles.css';

const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

// Local Device interface definition
interface Device {
  id: number;
  deviceCode: string;
  deviceName: string;
  data: any;
  isPay: number;
  project: number;
  addTime: string;
  deviceType: number;
  thirdType: string;
  area: number;
  beian: string;
  exData: {
    driverName?: string;
    idcardNumber?: string;
    monitorKey?: string;
    [key: string]: any;
  };
  deviceFactory: string;
  latitude: string;
  longitude: string;
  uid: string | null;
  price: number | null;
  isDel: number;
  remark: string | null;
}

// Local deviceTypeMap definition
const deviceTypeMap = {
  0: '塔吊',
  1: '扬尘',
  3: '视频',
  4: '卸料平台',
  5: '施工升降机'
};

interface DeviceFormProps {
  mode: 'create' | 'edit';
}

interface DeviceFormValues {
  deviceCode: string;
  deviceName: string;
  deviceType: number;
  deviceFactory: string;
  thirdType: string;
  beian: string;
  latitude: string;
  longitude: string;
  price: number | null;
  isPay: number;
  remark: string | null;
  // Add exData fields
  driverName: string;
  idcardNumber: string;
  monitorKey: string;
  area?: number | null;
  [key: string]: any; // Allow for dynamic fields
}

// 默认表单字段配置
const getDefaultFormFields = (): FormFieldConfig[] => {
  return [
    {
      key: 'area',
      label: '区域',
      type: 'input',
      required: false,
      props: { disabled: true }
    },
    {
      key: 'deviceCode',
      label: '设备ID',
      type: 'input',
      required: true,
      placeholder: '请输入设备ID',
      props: { maxLength: 128 },
      rules: [{ required: true, message: '请输入设备ID' }]
    },
    {
      key: 'deviceName',
      label: '设备名称',
      type: 'input',
      required: true,
      placeholder: '请输入设备名称',
      props: { maxLength: 255 },
      rules: [{ required: true, message: '请输入设备名称' }]
    },
    {
      key: 'deviceType',
      label: '设备类型',
      type: 'select',
      required: true,
      placeholder: '请选择设备类型',
      options: Object.entries(deviceTypeMap).map(([key, value]) => ({
        label: value,
        value: parseInt(key)
      })),
      rules: [{ required: true, message: '请选择设备类型' }]
    },
    {
      key: 'remark',
      label: '备注',
      type: 'textarea',
      required: false,
      placeholder: '请输入备注',
      props: { rows: 4, maxLength: 255 }
    }
  ];
};

const DeviceForm: React.FC<DeviceFormProps> = ({ mode }) => {
  const [form] = Form.useForm<DeviceFormValues>();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [projectName, setProjectName] = useState<string>('');
  const [projectArea, setProjectArea] = useState<number | null>(null);
  const [formConfig, setFormConfig] = useState<FormConfig | null>(null);
  const [initialValues, setInitialValues] = useState<Record<string, any>>({});
  
  const navigate = useNavigate();
  const { projectId, deviceId } = useParams<{ projectId: string; deviceId: string }>();
  const location = useLocation();
  
  // Reset form when mode changes
  useEffect(() => {
    // Reset form and initialValues when in create mode
    if (mode === 'create') {
      setInitialValues({});
      form.resetFields();
    }
    
    // Cleanup function to reset form when component unmounts
    return () => {
      form.resetFields();
      setInitialValues({});
    };
  }, [mode, form]);
  
  // Get project name and area from location state if available
  useEffect(() => {
    if (location.state) {
      if ('projectName' in location.state) {
        setProjectName(location.state.projectName as string);
      }
      
      // Check if area is available in location.state and fetch form config
      if ('area' in location.state && location.state.area) {
        setProjectArea(location.state.area as number);
        fetchFormConfig(location.state.area as number);
      }
    }
    
    if (projectId) {
      // Fetch project details to get the name and area
      getProjectById(parseInt(projectId))
        .then(response => {
          if (response.data.code === 0 && response.data.data) {
            setProjectName(response.data.data.name);
            
            // Only fetch form configuration based on area if not already fetched from location.state
            const area = response.data.data.area;
            if (area) {
              setProjectArea(area);
              if (!location.state || !('area' in location.state)) {
                fetchFormConfig(area);
              }
            }
          }
        })
        .catch(error => {
          console.error('Error fetching project details:', error);
        });
    }
  }, [projectId, location.state]);
  
  // Fetch form configuration based on area
  const fetchFormConfig = async (area: number) => {
    try {
      const formId = `device_${area}`;
      const response = await getFormConfigById(formId);
      
      if (response.data.code === 0 && response.data.data) {
        // 合并默认表单字段和动态表单字段
        const defaultFields = getDefaultFormFields();
        const dynamicFields = response.data.data.fields || [];
        
        // 创建一个映射来跟踪已经存在的字段键
        const fieldKeyMap = new Map<string, boolean>();
        defaultFields.forEach(field => fieldKeyMap.set(field.key, true));
        
        // 过滤掉与默认字段重复的动态字段
        const uniqueDynamicFields = dynamicFields.filter(field => !fieldKeyMap.has(field.key));
        
        // 合并字段
        const mergedConfig: FormConfig = {
          ...response.data.data,
          fields: [...defaultFields, ...uniqueDynamicFields]
        };
        
        setFormConfig(mergedConfig);
      } else {
        message.warning(`未找到区域 ${area} 的设备表单配置，将使用默认表单`);
      }
    } catch (error) {
      console.error('Error fetching form configuration:', error);
      message.error('获取表单配置失败，将使用默认表单');
    }
  };
  
  // Fetch device details if in edit mode
  useEffect(() => {
    // Reset form when switching between devices or modes
    if (mode === 'create') {
      setInitialValues({});
      form.resetFields();
    } else if (mode === 'edit' && deviceId) {
      setLoading(true);
      getDeviceById(parseInt(deviceId))
        .then(response => {
          if (response.data.code === 0 && response.data.data) {
            const device = response.data.data;
            
            // Prepare initial values for the form
            const values: Record<string, any> = {
              deviceCode: device.deviceCode,
              deviceName: device.deviceName,
              deviceType: device.deviceType,
              deviceFactory: device.deviceFactory,
              thirdType: device.thirdType,
              beian: device.beian,
              latitude: device.latitude,
              longitude: device.longitude,
              price: device.price,
              isPay: device.isPay,
              remark: device.remark,
            };
            
            // Add exData fields if they exist
            if (device.exData) {
              Object.keys(device.exData).forEach(key => {
                values[key] = device.exData[key];
              });
            }
            
            setInitialValues(values);
            form.resetFields(); // Reset form before setting new values
            form.setFieldsValue(values);
          } else {
            message.error(response.data.msg || '获取设备详情失败');
            navigate(-1);
          }
        })
        .catch(error => {
          console.error('Error fetching device details:', error);
          message.error('获取设备详情失败，请检查网络连接后重试');
          navigate(-1);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [mode, deviceId, form, navigate, projectArea]);
  
  const handleSubmit = async (values: Record<string, any>) => {
    if (!projectId) {
      message.error('项目ID不存在');
      return;
    }
    
    try {
      setSubmitting(true);
      
      // Extract standard fields and exData fields
      const { 
        deviceCode, 
        deviceName, 
        deviceType, 
        deviceFactory, 
        thirdType, 
        beian, 
        latitude, 
        longitude, 
        price, 
        isPay, 
        remark,
        area, // Extract area but don't include it in exDataFields
        ...exDataFields 
      } = values;
      
      // Prepare data for API
      const deviceData: Partial<Device> = {
        deviceCode,
        deviceName,
        deviceType,
        deviceFactory,
        thirdType,
        beian,
        latitude,
        longitude,
        price,
        isPay,
        remark,
        project: parseInt(projectId),
        exData: exDataFields,
      };
      
      let response;
      if (mode === 'edit' && deviceId) {
        // Update existing device
        response = await updateDevice(parseInt(deviceId), deviceData);
      } else {
        // Create new device
        response = await createDevice(deviceData);
      }
      
      if (response.data.code === 0) {
        message.success(mode === 'edit' ? '设备更新成功' : '设备创建成功');
        // Reset form data
        form.resetFields();
        setInitialValues({});
        // Navigate back to devices list
        navigate(`/devices/${projectId}`, { state: { projectName } });
      } else {
        message.error(response.data.msg || (mode === 'edit' ? '设备更新失败' : '设备创建失败'));
      }
    } catch (error) {
      console.error('表单提交错误:', error);
      message.error('表单提交失败，请检查输入内容');
    } finally {
      setSubmitting(false);
    }
  };
  
  const handleCancel = () => {
    navigate(`/devices/${projectId}`, { state: { projectName } });
  };
  
  // 创建默认表单配置
  const getDefaultFormConfig = (): FormConfig => {
    return {
      id: 'default_device_form',
      name: '设备表单',
      fields: getDefaultFormFields()
    };
  };
  
  return (
    <div className="device-form-container">
      <Card className="device-form-card">
        <div className="device-form-header">
          <Title level={4}>
            {mode === 'create' ? '新增设备' : '编辑设备'}
            {projectName && ` - ${projectName}`}
          </Title>
        </div>
        
        <Spin spinning={loading}>
          <DynamicForm
            form={form}
            config={formConfig || getDefaultFormConfig()}
            initialValues={{
              deviceType: 0,
              isPay: 0,
              area: projectArea,
              ...(mode === 'edit' ? initialValues : {})
            }}
            onFinish={handleSubmit}
            onCancel={handleCancel}
            loading={submitting}
            mode={mode}
          />
        </Spin>
      </Card>
    </div>
  );
};

export default DeviceForm; 